import { API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';
import { emptyVitals, sanitizeVitals } from '../utils/patientVitals';
import ClinicalReports from './ClinicalReports';
import SearchBox from './SearchBox';

function rupee(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '₹0';
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function medicineChoice(medicine) {
  const stock = medicine.STATUS === 'Expired' ? 'Expired' : `Stock ${medicine.QUANTITY_IN_STOCK ?? 0}`;
  const generic = medicine.GENERIC_NAME && medicine.GENERIC_NAME.toLowerCase() !== String(medicine.MEDICINE_NAME || '').toLowerCase()
    ? medicine.GENERIC_NAME
    : '';
  return {
    key: medicine._id,
    label: [medicine.MEDICINE_NAME, medicine.STRENGTH, medicine.MEDICINE_TYPE, generic, stock, rupee(medicine.SELLING_PRICE)].filter(Boolean).join(' · '),
    item: medicine,
  };
}

function procedureChoice(procedure) {
  const sittings = procedure.SESSION_COUNT > 1 ? `${procedure.SESSION_COUNT} sittings` : '';
  const kind = procedure.SERVICE_KIND === 'Package'
    ? ['Package', sittings].filter(Boolean).join(', ')
    : procedure.SERVICE_KIND === 'Course'
      ? ['Course', sittings].filter(Boolean).join(', ')
      : 'Single visit';
  const price = procedure.BILLING_MODE === 'Once' ? `${rupee(procedure.COST)} once` : `${rupee(procedure.COST)} / sitting`;
  return {
    key: procedure._id,
    label: [procedure.PROCEDURE_NAME, procedure.PROCEDURE_TYPE, procedure.DURATION ? `${procedure.DURATION} min` : '', kind, price].filter(Boolean).join(' · '),
    item: procedure,
  };
}

function labChoice(test) {
  return {
    key: test._id || test.LAB_TEST_ID,
    label: [
      test.TEST_NAME,
      test.TEST_CODE,
      test.TEST_CATEGORY,
      test.SAMPLE_TYPE,
      test.FASTING_REQUIRED ? 'Fasting' : '',
      test.NORMAL_RANGE ? `Range ${test.NORMAL_RANGE}` : '',
      rupee(test.COST),
    ].filter(Boolean).join(' · '),
    item: test,
  };
}

const DoctorAppointmentDetailModal = ({ appointment, onClose, onSave, readOnly = false }) => {
  // 1. Call all hooks unconditionally with safe default values.
  const [doctorComments, setDoctorComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // State for editable appointment details
  const [editableDate, setEditableDate] = useState('');
  const [editableTime, setEditableTime] = useState('');
  const [editablePlace, setEditablePlace] = useState('');
  const [editableStatus, setEditableStatus] = useState('');
  const [editableSymptoms, setEditableSymptoms] = useState('');

  // State for Medicine Plans
  const [availableMedicines, setAvailableMedicines] = useState([]);
  const [medicineLoading, setMedicineLoading] = useState(true);
  const [medicineError, setMedicineError] = useState('');
  const [newMedicinePlan, setNewMedicinePlan] = useState({
    MEDICINE_ID: '',
    MEDICINE_DOSAGE: '',
    DOSAGE_UNIT: '',
    DOSAGE_FREQUENCY: '',
    DOSAGE_TIME: [],
    DURATION: '',
    MEDICINE_ROUTE: '',
    INSTRUCTIONS: '',
    SIDE_EFFECTS: '',
    REFILLABLE: false,
    REFILL_COUNT: 0,
    STATUS: 'Active',
  });
  const [currentMedicinePlans, setCurrentMedicinePlans] = useState([]);

  // State for Procedure Plans
  const [availableProcedures, setAvailableProcedures] = useState([]);
  const [procedureLoading, setProcedureLoading] = useState(true);
  const [procedureError, setProcedureError] = useState('');
  const [newProcedurePlan, setNewProcedurePlan] = useState({
    PROCEDURE_ID: '',
    PROCEDURE_STATUS: 'Scheduled',
    PROCEDURE_DATE: '',
    PROCEDURE_SLOT: '',
    ROOM_ID: '',
    ROOM_NAME: '',
    THERAPIST_ID: '',
    THERAPIST_NAME: '',
  });
  const [currentProcedurePlans, setCurrentProcedurePlans] = useState([]);

  // State for Rooms and Therapists
  const [availableProcedureRooms, setAvailableProcedureRooms] = useState([]);
  const [roomLoading, setRoomLoading] = useState(true);
  const [roomError, setRoomError] = useState('');
  const [availableTherapists, setAvailableTherapists] = useState([]);
  const [therapistLoading, setTherapistLoading] = useState(true);
  const [therapistError, setTherapistError] = useState('');

  // State for Lab Tests
  const [availableLabTests, setAvailableLabTests] = useState([]);
  const [labTestLoading, setLabTestLoading] = useState(true);
  const [labTestError, setLabTestError] = useState('');
  const [newLabTestPlan, setNewLabTestPlan] = useState({
    LAB_TEST_ID: '',
    TEST_STATUS: 'Ordered',
    TEST_DATE: '',
    TEST_SLOT: '',
    PRIORITY: 'Normal',
    INSTRUCTIONS: '',
    FASTING_REQUIRED: false,
    PREPARATION_NOTES: '',
    REFERENCE_LAB: '',
    COST: 0,
    INSURANCE_COVERED: false,
    RESULTS_RECEIVED: false,
    RESULTS_DATE: '',
    RESULTS_NOTES: ''
  });
  const [currentLabTestPlans, setCurrentLabTestPlans] = useState([]);
  const [visitVitals, setVisitVitals] = useState(emptyVitals());
  const [history, setHistory] = useState([]);
  const [bills, setBills] = useState({ invoices: [], sales: [] });
  const [chartLoading, setChartLoading] = useState(false);
  const [showMedicineForm, setShowMedicineForm] = useState(false);
  const [showProcedureForm, setShowProcedureForm] = useState(false);
  const [showLabForm, setShowLabForm] = useState(false);
  const [medicineQuery, setMedicineQuery] = useState('');
  const [procedureQuery, setProcedureQuery] = useState('');
  const [labQuery, setLabQuery] = useState('');

  // Helper function to fetch data and update state consistently
  const fetchData = async (url, setData, setError, setLoading, authToken) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      const data = await response.json();
      if (response.ok) {
        setData(Array.isArray(data) ? data : []);
      } else {
        setError(data.message || 'Failed to fetch data.');
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 2. Synchronize state with props inside a useEffect.
    if (appointment) {
      setDoctorComments(appointment.DOCTOR_COMMENTS || '');
      setEditableDate(appointment.APPOINTMENT_DATE ? new Date(appointment.APPOINTMENT_DATE).toISOString().split('T')[0] : '');
      setEditableTime(appointment.APPOINTMENT_SLOT || '');
      setEditablePlace(appointment.APPOINTMENT_PLACE || '');
      setEditableStatus(appointment.APPOINTMENT_STATUS || '');
      setEditableSymptoms(appointment.PATIENT_SYMPTOMS || '');
      const visitDate = appointment.APPOINTMENT_DATE ? new Date(appointment.APPOINTMENT_DATE).toISOString().split('T')[0] : '';
      setNewProcedurePlan((current) => ({
        ...current,
        PROCEDURE_DATE: current.PROCEDURE_DATE || visitDate,
        PROCEDURE_SLOT: current.PROCEDURE_SLOT || appointment.APPOINTMENT_SLOT || ''
      }));
      setCurrentMedicinePlans(appointment.DR_MEDICINES || []);
      setCurrentProcedurePlans(appointment.DR_PROCEDURES || []);
      setCurrentLabTestPlans(appointment.DR_LAB_TESTS || []);
      const registered = (appointment.PATIENT_ID && appointment.PATIENT_ID.VITALS) || {};
      const recorded = appointment.VITALS || {};
      setVisitVitals({
        BLOOD_PRESSURE: recorded.BLOOD_PRESSURE || registered.BLOOD_PRESSURE || '',
        PULSE: recorded.PULSE ?? registered.PULSE ?? '',
        TEMPERATURE: recorded.TEMPERATURE ?? registered.TEMPERATURE ?? '',
        WEIGHT: recorded.WEIGHT ?? registered.WEIGHT ?? '',
        HEIGHT: recorded.HEIGHT ?? registered.HEIGHT ?? '',
        SPO2: recorded.SPO2 ?? registered.SPO2 ?? '',
        RESPIRATORY_RATE: recorded.RESPIRATORY_RATE ?? registered.RESPIRATORY_RATE ?? ''
      });
      const token = localStorage.getItem('token');
      const patientId = appointment.PATIENT_ID && (appointment.PATIENT_ID._id || appointment.PATIENT_ID);
      if (token && patientId) {
        setChartLoading(true);
        Promise.all([
          fetch(`${API_BASE_URL}/appointments/patient/${patientId}`, { headers: { Authorization: `Bearer ${token}` } }).then((response) => response.ok ? response.json() : []),
          fetch(`${API_BASE_URL}/doctors/patients/${patientId}/chart`, { headers: { Authorization: `Bearer ${token}` } }).then((response) => response.ok ? response.json() : { invoices: [], sales: [] })
        ]).then(([visits, chart]) => {
          setHistory(Array.isArray(visits) ? visits : []);
          setBills(chart && chart.invoices ? chart : { invoices: [], sales: [] });
        }).catch(() => {
          setHistory([]);
          setBills({ invoices: [], sales: [] });
        }).finally(() => setChartLoading(false));
      }

      if (!token) {
        const errorMessage = 'Authentication token not found. Please log in again.';
        setMedicineError(errorMessage);
        setProcedureError(errorMessage);
        setRoomError(errorMessage);
        setTherapistError(errorMessage);
        setLabTestError(errorMessage);
        setMedicineLoading(false);
        setProcedureLoading(false);
        setRoomLoading(false);
        setTherapistLoading(false);
        setLabTestLoading(false);
        return;
      }

      // Fetch data using the helper function
      fetchData(`${API_BASE_URL}/medicines`, setAvailableMedicines, setMedicineError, setMedicineLoading, token);
      fetchData(`${API_BASE_URL}/procedures`, setAvailableProcedures, setProcedureError, setProcedureLoading, token);
      fetchData(`${API_BASE_URL}/procedureRooms`, setAvailableProcedureRooms, setRoomError, setRoomLoading, token);
      fetchData(`${API_BASE_URL}/therapists`, setAvailableTherapists, setTherapistError, setTherapistLoading, token);
      // Fetch lab tests with custom handling since API returns { labTests: [...] }
      const fetchLabTests = async () => {
        setLabTestLoading(true);
        setLabTestError('');
        try {
          const response = await fetch(`${API_BASE_URL}/labTests`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          const data = await response.json();
          if (response.ok) {
            setAvailableLabTests(data.labTests || []);
            console.log('Lab tests fetched:', data.labTests?.length || 0);
          } else {
            setLabTestError(data.message || 'Failed to fetch lab tests.');
          }
        } catch (err) {
          setLabTestError(`Network error: ${err.message}`);
        } finally {
          setLabTestLoading(false);
        }
      };
      fetchLabTests();
    }
  }, [appointment]);

  // 3. Now that all hooks have been called, we can have our conditional return.
  if (!appointment) {
    return null;
  }

  const patient = appointment.PATIENT_ID || {};
  const selectedCourse = availableProcedures.find((procedure) => procedure._id === newProcedurePlan.PROCEDURE_ID);
  const sourceVisits = history.length ? history : [{ APPOINTMENT_DATE: appointment.APPOINTMENT_DATE, DR_MEDICINES: currentMedicinePlans }];
  const activeMedicines = [];
  sourceVisits.forEach((visit) => {
    (visit.DR_MEDICINES || []).forEach((plan) => {
      if (plan && (!plan.STATUS || plan.STATUS === 'Active')) {
        activeMedicines.push({ plan, date: visit.APPOINTMENT_DATE || plan.PRESCRIPTION_DATE });
      }
    });
  });
  const weight = Number(visitVitals.WEIGHT);
  const height = Number(visitVitals.HEIGHT);
  const bmi = weight > 0 && height > 0 ? (weight / ((height / 100) ** 2)).toFixed(1) : '';
  const billRows = [
    ...(bills.invoices || []).map((invoice) => ({
      key: invoice._id || invoice.INVOICE_ID,
      date: invoice.INVOICE_DATE,
      kind: 'Invoice',
      ref: invoice.INVOICE_ID,
      status: invoice.INVOICE_STATUS,
      payment: `${invoice.PAYMENT_STATUS || ''} ${invoice.PAYMENT_METHOD || ''}`.trim(),
      items: (invoice.LINE_ITEMS || []).map((item) => item.ITEM_NAME).filter(Boolean).join(', '),
      tax: invoice.TAX_AMOUNT,
      total: invoice.TOTAL_AMOUNT
    })),
    ...(bills.sales || []).map((sale) => ({
      key: sale._id || sale.SALE_ID,
      date: sale.SALE_DATE,
      kind: sale.SALE_TYPE || 'Sale',
      ref: sale.SALE_ID,
      status: sale.SALE_STATUS,
      payment: `${sale.PAYMENT_STATUS || ''} ${sale.PAYMENT_METHOD || ''}`.trim(),
      items: (sale.LINE_ITEMS || []).map((item) => item.ITEM_NAME).filter(Boolean).join(', '),
      tax: sale.TAX_AMOUNT,
      total: sale.TOTAL_AMOUNT
    }))
  ].sort((left, right) => new Date(right.date) - new Date(left.date));

  const handleChange = (e) => {
    setDoctorComments(e.target.value);
  };

  const handleAppointmentDetailChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case 'APPOINTMENT_DATE':
        setEditableDate(value);
        break;
      case 'APPOINTMENT_SLOT':
        setEditableTime(value);
        break;
      case 'APPOINTMENT_PLACE':
        setEditablePlace(value);
        break;
      case 'APPOINTMENT_STATUS':
        setEditableStatus(value);
        break;
      case 'PATIENT_SYMPTOMS':
        setEditableSymptoms(value);
        break;
      default:
        break;
    }
  };

  const handleNewMedicinePlanChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewMedicinePlan((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleNewProcedurePlanChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === 'ROOM_ID') {
      const selectedRoom = availableProcedureRooms.find(room => room._id === value);
      setNewProcedurePlan(prev => ({
        ...prev,
        ROOM_ID: value,
        ROOM_NAME: selectedRoom ? selectedRoom.ROOM_NAME : '',
      }));
    } else if (name === 'THERAPIST_ID') {
      const selectedTherapist = availableTherapists.find(therapist => therapist._id === value);
      setNewProcedurePlan(prev => ({
        ...prev,
        THERAPIST_ID: value,
        THERAPIST_NAME: selectedTherapist ? selectedTherapist.THERAPIST_NAME : '',
      }));
    } else {
      setNewProcedurePlan((prev) => ({
        ...prev,
        [name]: updatedValue,
      }));
    }
  };

  const handleNewLabTestPlanChange = (e) => {
    const { name, value, type, checked } = e.target;
    let updatedValue = value;

    if (name === 'LAB_TEST_ID') {
      const selectedTest = availableLabTests.find(test => test.LAB_TEST_ID === value);
      setNewLabTestPlan(prev => ({
        ...prev,
        LAB_TEST_ID: value,
        COST: selectedTest ? selectedTest.COST : 0,
        FASTING_REQUIRED: selectedTest ? selectedTest.FASTING_REQUIRED : false,
        PREPARATION_NOTES: selectedTest ? selectedTest.PREPARATION_REQUIRED : '',
        REFERENCE_LAB: selectedTest ? selectedTest.REFERENCE_LAB : '',
      }));
    } else if (type === 'checkbox') {
      setNewLabTestPlan(prev => ({
        ...prev,
        [name]: checked,
      }));
    } else if (name === 'COST') {
      setNewLabTestPlan(prev => ({
        ...prev,
        [name]: Number(value),
      }));
    } else {
      setNewLabTestPlan((prev) => ({
        ...prev,
        [name]: updatedValue,
      }));
    }
  };

  const handleSaveDiagnosis = async () => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setLoading(false);
      return;
    }

    let vitalsPayload;
    try {
      vitalsPayload = sanitizeVitals(visitVitals);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/doctors/appointments/${appointment._id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            DOCTOR_COMMENTS: doctorComments,
            APPOINTMENT_DATE: editableDate,
            APPOINTMENT_SLOT: editableTime,
            APPOINTMENT_PLACE: editablePlace,
            APPOINTMENT_STATUS: editableStatus,
            PATIENT_SYMPTOMS: editableSymptoms,
            VITALS: vitalsPayload,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert('Diagnosis saved successfully!');
        onSave();
      } else {
        setError(data.message || 'Failed to save diagnosis.');
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMedicinePlan = async () => {
    if (!newMedicinePlan.MEDICINE_ID) {
      setError('Select a medicine.');
      return;
    }
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setLoading(false);
      return;
    }

    try {
        const response = await fetch(`${API_BASE_URL}/doctors/appointments/${appointment._id}/medicine-plan`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                ...newMedicinePlan,
                PATIENT_ID: appointment.PATIENT_ID._id,
                DOCTOR_ID: appointment.DOCTOR_ID,
            }),
        });

        const data = await response.json();

        if (response.ok) {
            alert('Medicine plan added successfully!');
            onSave();
            setNewMedicinePlan({
              MEDICINE_ID: '',
              MEDICINE_DOSAGE: '',
              DOSAGE_UNIT: '',
              DOSAGE_FREQUENCY: '',
              DOSAGE_TIME: [],
              DURATION: '',
              MEDICINE_ROUTE: '',
              INSTRUCTIONS: '',
              SIDE_EFFECTS: '',
              REFILLABLE: false,
              REFILL_COUNT: 0,
              STATUS: 'Active',
            });
            setMedicineQuery('');
        } else {
            setError(data.message || 'Failed to add medicine plan.');
        }
    } catch (err) {
        setError(`Network error: ${err.message}`);
    } finally {
        setLoading(false);
    }
};

  const handleAddProcedurePlan = async () => {
    if (!newProcedurePlan.PROCEDURE_ID) {
      setError('Select a procedure.');
      return;
    }
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setLoading(false);
      return;
    }

    try {
        const response = await fetch(`${API_BASE_URL}/doctors/appointments/${appointment._id}/procedure-plan`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                ...newProcedurePlan,
                PATIENT_ID: appointment.PATIENT_ID._id,
                PROCEDURE_NAME: availableProcedures.find(p => p._id === newProcedurePlan.PROCEDURE_ID)?.PROCEDURE_NAME || '',
            }),
        });

        const data = await response.json();

        if (response.ok) {
            alert('Procedure plan added successfully!');
            onSave();
            setNewProcedurePlan({
              PROCEDURE_ID: '',
              PROCEDURE_STATUS: 'Scheduled',
              PROCEDURE_DATE: '',
              PROCEDURE_SLOT: '',
              ROOM_ID: '',
              ROOM_NAME: '',
              THERAPIST_ID: '',
              THERAPIST_NAME: '',
            });
            setProcedureQuery('');
        } else {
            setError(data.message || 'Failed to add procedure plan.');
        }
    } catch (err) {
        setError(`Network error: ${err.message}`);
    } finally {
        setLoading(false);
    }
  };

  const handleAddLabTestPlan = async () => {
    console.log('handleAddLabTestPlan called');
    console.log('newLabTestPlan:', newLabTestPlan);
    console.log('appointment._id:', appointment._id);
    
    // Validate required fields
    if (!newLabTestPlan.LAB_TEST_ID) {
      setError('Please select a lab test.');
      return;
    }
    
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      const requestBody = {
        ...newLabTestPlan,
        APPOINTMENT_ID: appointment._id,
      };
      
      console.log('Sending request body:', requestBody);
      
      const response = await fetch(`${API_BASE_URL}/labTestPlans`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        setCurrentLabTestPlans([...currentLabTestPlans, data.labTestPlan]);
        setNewLabTestPlan({
          LAB_TEST_ID: '',
          TEST_STATUS: 'Ordered',
          TEST_DATE: '',
          TEST_SLOT: '',
          PRIORITY: 'Normal',
          INSTRUCTIONS: '',
          FASTING_REQUIRED: false,
          PREPARATION_NOTES: '',
          REFERENCE_LAB: '',
          COST: 0,
          INSURANCE_COVERED: false,
          RESULTS_RECEIVED: false,
          RESULTS_DATE: '',
          RESULTS_NOTES: ''
        });
        setLabQuery('');
        console.log('Lab test plan added successfully');
        onSave(); // Refresh appointment data in parent component
      } else {
        setError(data.message || 'Failed to add lab test plan.');
        console.error('Error response:', data);
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
      console.error('Network error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle} className="visit-chart">
        <style>{'.visit-chart label{display:block;font-size:.85rem;color:#495057;margin-bottom:.15rem}'}</style>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ margin: '0 0 0.25rem' }}>{patient.PATIENT_NAME || 'Patient'}</h3>
            <p style={{ margin: 0, color: '#495057' }}>
              {[patient.PATIENT_AGE ? `${patient.PATIENT_AGE} yrs` : '', patient.PATIENT_GENDER, patient.BLOOD_TYPE ? `Blood ${patient.BLOOD_TYPE}` : '', patient.PATIENT_PHONE].filter(Boolean).join(' · ')}
            </p>
            <p style={{ margin: '0.25rem 0 0', color: '#6c757d', fontSize: '0.9rem' }}>{appointment.APPOINTMENT_ID} · {appointment.APPOINTMENT_TYPE || 'Visit'} · {appointment.CONSULTATION_MODE || 'In-person'}</p>
          </div>
          <button type="button" onClick={onClose} style={{ padding: '8px 14px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
        </div>
        {patient.ALLERGIES && <p style={{ margin: '0.75rem 0 0', padding: '8px 10px', backgroundColor: '#fff3cd', borderRadius: '4px' }}><strong>Allergies:</strong> {patient.ALLERGIES}</p>}
        {patient.MEDICAL_HISTORY && patient.MEDICAL_HISTORY !== 'SAMPLE-YEAR' && <p style={{ margin: '0.5rem 0 0', color: '#495057' }}><strong>History:</strong> {patient.MEDICAL_HISTORY}</p>}

        <section style={panelStyle}>
          <h4 style={sectionTitle}>This visit</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
            <label>Date
              <input type="date" name="APPOINTMENT_DATE" value={editableDate} onChange={handleAppointmentDetailChange} style={fieldStyle} />
            </label>
            <label>Time
              <input type="text" name="APPOINTMENT_SLOT" value={editableTime} onChange={handleAppointmentDetailChange} placeholder="10:00 AM - 10:30 AM" style={fieldStyle} />
            </label>
            <label>Place
              <input type="text" name="APPOINTMENT_PLACE" value={editablePlace} onChange={handleAppointmentDetailChange} style={fieldStyle} />
            </label>
            <label>Status
              <select name="APPOINTMENT_STATUS" value={editableStatus} onChange={handleAppointmentDetailChange} style={fieldStyle}>
                <option value="Scheduled">Scheduled</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
                <option value="Rescheduled">Rescheduled</option>
              </select>
            </label>
          </div>
          <h4 style={{ ...sectionTitle, marginTop: '1rem' }}>Vitals for this visit</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
            {[
              ['BLOOD_PRESSURE', 'Blood pressure', '120/80'],
              ['PULSE', 'Pulse', '72'],
              ['TEMPERATURE', 'Temp °F', '98.6'],
              ['WEIGHT', 'Weight kg', '68'],
              ['HEIGHT', 'Height cm', '170'],
              ['SPO2', 'SpO2 %', '98'],
              ['RESPIRATORY_RATE', 'Resp. rate', '16']
            ].map(([key, label, placeholder]) => (
              <label key={key}>{label}
                <input value={visitVitals[key]} placeholder={placeholder} onChange={(event) => setVisitVitals((current) => ({ ...current, [key]: event.target.value }))} style={fieldStyle} readOnly={readOnly} />
              </label>
            ))}
            <label>BMI
              <input value={bmi} readOnly style={{ ...fieldStyle, backgroundColor: '#f8f9fa' }} />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
            <label>Symptoms
              <textarea name="PATIENT_SYMPTOMS" value={editableSymptoms} onChange={handleAppointmentDetailChange} rows="4" readOnly={readOnly} style={fieldStyle} />
            </label>
            <label>Diagnosis and advice
              <textarea value={doctorComments} onChange={handleChange} rows="4" readOnly={readOnly} placeholder="Diagnosis, advice, and follow-up" style={fieldStyle} />
            </label>
          </div>
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {!readOnly && (
            <button type="button" onClick={handleSaveDiagnosis} disabled={loading} style={{ marginTop: '0.75rem', padding: '10px 18px', backgroundColor: '#1f4d40', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              {loading ? 'Saving...' : 'Save visit'}
            </button>
          )}
        </section>

        <section style={panelStyle}>
          <h4 style={sectionTitle}>Active medicine plan</h4>
          {activeMedicines.length ? (
            <ChartTable
              headers={['Medicine', 'Dose', 'Frequency', 'Duration', 'Route', 'Since']}
              rows={activeMedicines.map(({ plan, date }) => [
                plan.MEDICINE_ID?.MEDICINE_NAME || 'Medicine',
                `${plan.MEDICINE_DOSAGE || ''} ${plan.DOSAGE_UNIT || ''}`.trim(),
                `${plan.DOSAGE_FREQUENCY || ''}${(plan.DOSAGE_TIME || []).length ? ` (${plan.DOSAGE_TIME.join(', ')})` : ''}`,
                plan.DURATION ? `${plan.DURATION} days` : '',
                plan.MEDICINE_ROUTE || '',
                date ? new Date(date).toLocaleDateString() : ''
              ])}
            />
          ) : <p style={{ margin: 0, color: '#6c757d' }}>No active medicine plan.</p>}
        </section>

        <section style={panelStyle}>
          <h4 style={sectionTitle}>Appointment history</h4>
          {chartLoading ? <p>Loading history...</p> : (
            <ChartTable
              headers={['Date', 'Time', 'Doctor', 'Status', 'Vitals', 'Symptoms', 'Diagnosis', 'Medicines']}
              rows={(history.length ? history : []).map((visit) => [
                visit.APPOINTMENT_DATE ? new Date(visit.APPOINTMENT_DATE).toLocaleDateString() : '',
                visit.APPOINTMENT_SLOT || '',
                visit.DOCTOR_ID?.DOCTOR_NAME || visit.DOCTOR_NAME || '',
                visit.APPOINTMENT_STATUS || '',
                vitalSummary(visit.VITALS),
                visit.PATIENT_SYMPTOMS || '',
                visit.DOCTOR_COMMENTS || '',
                (visit.DR_MEDICINES || []).map((plan) => plan.MEDICINE_ID?.MEDICINE_NAME || 'Medicine').join(', ')
              ])}
            />
          )}
        </section>

        <section style={panelStyle}>
          <h4 style={sectionTitle}>Billing</h4>
          {chartLoading ? <p>Loading bills...</p> : (
            <ChartTable
              headers={['Date', 'Type', 'Reference', 'Status', 'Payment', 'Items', 'Tax', 'Total']}
              rows={billRows.map((row) => [
                row.date ? new Date(row.date).toLocaleDateString() : '',
                row.kind,
                row.ref || '',
                row.status || '',
                row.payment || '',
                row.items || '',
                `₹${(Number(row.tax) || 0).toFixed(2)}`,
                `₹${(Number(row.total) || 0).toFixed(2)}`
              ])}
            />
          )}
        </section>

        <ClinicalReports
          patientId={patient._id || appointment.PATIENT_ID}
          appointmentId={appointment._id}
        />

        {/* Medicine Plans Section */}
        <div style={{ marginTop: '30px' }}>
          <h4 style={sectionTitle}>Prescribe on this visit</h4>
          {medicineLoading && <p>Loading medicines...</p>}
          {medicineError && <p style={{ color: 'red' }}>Error loading medicines: {medicineError}</p>}

          {currentMedicinePlans.length > 0 ? (
            <div>
              <h5>Existing Medicine Plans:</h5>
              {currentMedicinePlans.map((plan) => (
                <div key={plan._id} style={{ border: '1px dashed #ccc', padding: '10px', marginBottom: '10px' }}>
                  <p><strong>Medicine:</strong> {plan.MEDICINE_ID ? `${plan.MEDICINE_ID.MEDICINE_NAME} (${plan.MEDICINE_ID.STRENGTH})` : 'N/A'}</p>
                  <p><strong>Dosage:</strong> {plan.MEDICINE_DOSAGE} {plan.DOSAGE_UNIT}</p>
                  <p><strong>Frequency:</strong> {plan.DOSAGE_FREQUENCY}</p>
                  <p><strong>Time:</strong> {plan.DOSAGE_TIME.join(', ')}</p>
                  <p><strong>Duration:</strong> {plan.DURATION} days</p>
                  <p><strong>Route:</strong> {plan.MEDICINE_ROUTE}</p>
                  {plan.INSTRUCTIONS && <p><strong>Instructions:</strong> {plan.INSTRUCTIONS}</p>}
                  {plan.SIDE_EFFECTS && <p><strong>Side Effects:</strong> {plan.SIDE_EFFECTS}</p>}
                  <p><strong>Refillable:</strong> {plan.REFILLABLE ? 'Yes' : 'No'}</p>
                  {plan.REFILLABLE && <p><strong>Refill Count:</strong> {plan.REFILL_COUNT}</p>}
                  <p><strong>Status:</strong> {plan.STATUS}</p>
                  <button onClick={() => console.log('View Medicine Plan:', plan)} style={{ marginTop: '5px', padding: '5px 10px', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    View Plan
                  </button>
                </div>
              ))}
            </div>
          ) : (
            !medicineLoading && !medicineError && currentMedicinePlans.length === 0 && <p style={{ color: '#666', marginTop: 0 }}>Nothing prescribed on this visit yet. Active medicines from earlier visits are listed above.</p>
          )}

          {!readOnly && (
            <div style={{ marginTop: '20px', border: '1px solid #007bff', padding: '15px', borderRadius: '8px', backgroundColor: '#e6f7ff' }}>
              <button type="button" onClick={() => setShowMedicineForm((open) => !open)} style={addButton}>
                {showMedicineForm ? 'Hide medicine form' : 'Add medicine'}
              </button>
              {showMedicineForm && <>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Medicine:</label>
              <SearchBox
                placeholder={medicineLoading ? 'Loading medicines...' : 'Search medicine by name, stock, or price'}
                value={medicineQuery}
                onChange={(event) => {
                  const value = event.target.value;
                  setMedicineQuery(value);
                  const selected = availableMedicines.find((medicine) => medicine._id === newMedicinePlan.MEDICINE_ID);
                  if (!selected || medicineChoice(selected).label !== value) {
                    setNewMedicinePlan((prev) => ({ ...prev, MEDICINE_ID: '' }));
                  }
                }}
                options={availableMedicines.map(medicineChoice)}
                onSelect={(option) => {
                  setMedicineQuery(option.label);
                  setNewMedicinePlan((prev) => ({ ...prev, MEDICINE_ID: option.item._id }));
                }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Dosage (Number):</label>
              <input
                type="number"
                name="MEDICINE_DOSAGE"
                value={newMedicinePlan.MEDICINE_DOSAGE}
                onChange={handleNewMedicinePlanChange}
                placeholder="e.g., 2"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Dosage Unit:</label>
              <select
                name="DOSAGE_UNIT"
                value={newMedicinePlan.DOSAGE_UNIT}
                onChange={handleNewMedicinePlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">Select Unit</option>
                <option value="mg">mg</option>
                <option value="ml">ml</option>
                <option value="tablet">tablet</option>
                <option value="capsule">capsule</option>
                <option value="drop">drop</option>
                <option value="syrup">syrup</option>
                <option value="injection">injection</option>
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Frequency:</label>
              <select
                name="DOSAGE_FREQUENCY"
                value={newMedicinePlan.DOSAGE_FREQUENCY}
                onChange={handleNewMedicinePlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">Select Frequency</option>
                <option value="Once Daily">Once Daily</option>
                <option value="Twice Daily">Twice Daily</option>
                <option value="Thrice Daily">Thrice Daily</option>
                <option value="As Needed">As Needed</option>
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Dosage Time (select all that apply):</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {['Morning', 'Afternoon', 'Evening', 'Night'].map(time => (
                  <label key={time}>
                    <input
                      type="checkbox"
                      name="DOSAGE_TIME"
                      value={time}
                      checked={newMedicinePlan.DOSAGE_TIME.includes(time)}
                      onChange={(e) => {
                        const { value, checked } = e.target;
                        setNewMedicinePlan(prev => ({
                          ...prev,
                          DOSAGE_TIME: checked
                            ? [...prev.DOSAGE_TIME, value]
                            : prev.DOSAGE_TIME.filter(t => t !== value),
                        }));
                      }}
                      style={{ marginRight: '5px' }}
                    />
                    {time}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Duration (in days):</label>
              <input
                type="number"
                name="DURATION"
                value={newMedicinePlan.DURATION}
                onChange={handleNewMedicinePlanChange}
                placeholder="e.g., 7"
                min="1"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Medicine Route:</label>
              <select
                name="MEDICINE_ROUTE"
                value={newMedicinePlan.MEDICINE_ROUTE}
                onChange={handleNewMedicinePlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">Select Route</option>
                <option value="Oral">Oral</option>
                <option value="Injection">Injection</option>
                <option value="Topical">Topical</option>
                <option value="Inhalation">Inhalation</option>
                <option value="Sublingual">Sublingual</option>
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Instructions:</label>
              <textarea
                name="INSTRUCTIONS"
                value={newMedicinePlan.INSTRUCTIONS}
                onChange={handleNewMedicinePlanChange}
                placeholder="Special instructions for the patient..."
                rows="3"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              ></textarea>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Side Effects:</label>
              <textarea
                name="SIDE_EFFECTS"
                value={newMedicinePlan.SIDE_EFFECTS}
                onChange={handleNewMedicinePlanChange}
                placeholder="Potential side effects..."
                rows="2"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              ></textarea>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <input
                type="checkbox"
                name="REFILLABLE"
                checked={newMedicinePlan.REFILLABLE}
                onChange={handleNewMedicinePlanChange}
                style={{ marginRight: '5px' }}
              />
              <label>Refillable</label>
            </div>
            {newMedicinePlan.REFILLABLE && (
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Refill Count:</label>
                <input
                  type="number"
                  name="REFILL_COUNT"
                  value={newMedicinePlan.REFILL_COUNT}
                  onChange={handleNewMedicinePlanChange}
                  min="0"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>
            )}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Status:</label>
              <select
                name="STATUS"
                value={newMedicinePlan.STATUS}
                onChange={handleNewMedicinePlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="Active">Active</option>
                <option value="Completed">Completed</option>
                <option value="Discontinued">Discontinued</option>
              </select>
            </div>

              <button onClick={handleAddMedicinePlan} disabled={loading} style={{ marginTop: '10px', padding: '10px 20px', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
                {loading ? 'Adding...' : 'Add Medicine Plan'}
              </button>
              </>}
            </div>
          )}
        </div>

        {/* Procedure Plans Section */}
        <div style={{ marginTop: '30px' }}>
          <h4 style={sectionTitle}>Procedures on this visit</h4>
          {procedureLoading && <p>Loading procedures...</p>}
          {procedureError && <p style={{ color: 'red' }}>Error loading procedures: {procedureError}</p>}

          {currentProcedurePlans.length > 0 ? (
            <div>
              <h5>Existing Procedure Plans:</h5>
              {currentProcedurePlans.map((plan) => (
                <div key={plan._id} style={{ border: '1px dashed #ccc', padding: '10px', marginBottom: '10px' }}>
                  <p><strong>Procedure:</strong> {plan.PROCEDURE_ID ? `${plan.PROCEDURE_ID.PROCEDURE_NAME} (${plan.PROCEDURE_ID.PROCEDURE_TYPE})` : 'N/A'}</p>
                  <p><strong>Status:</strong> {plan.PROCEDURE_STATUS}</p>
                  {plan.SESSION_TOTAL > 1 && <p><strong>Sittings:</strong> {plan.SESSION_NUMBER || 1} of {plan.SESSION_TOTAL}</p>}
                  <p><strong>Date:</strong> {new Date(plan.PROCEDURE_DATE).toLocaleDateString()}</p>
                  <p><strong>Slot:</strong> {plan.PROCEDURE_SLOT}</p>
                  {plan.ROOM_NAME && <p><strong>Room:</strong> {plan.ROOM_NAME}</p>}
                  {plan.THERAPIST_NAME && <p><strong>Therapist:</strong> {plan.THERAPIST_NAME}</p>}
                  <button onClick={() => console.log('View Procedure Plan:', plan)} style={{ marginTop: '5px', padding: '5px 10px', backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                    View Plan
                  </button>
                </div>
              ))}
            </div>
          ) : (
            !procedureLoading && !procedureError && currentProcedurePlans.length === 0 && <p style={{ color: '#666', marginTop: 0 }}>No procedure recorded on this visit.</p>
          )}

          {!readOnly && (
            <div style={{ marginTop: '20px', border: '1px solid #6f42c1', padding: '15px', borderRadius: '8px', backgroundColor: '#f8f0ff' }}>
              <button type="button" onClick={() => setShowProcedureForm((open) => !open)} style={addButton}>
                {showProcedureForm ? 'Hide procedure form' : 'Add procedure'}
              </button>
              {showProcedureForm && <>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Procedure:</label>
              <SearchBox
                placeholder={procedureLoading ? 'Loading procedures...' : 'Search procedure by name, type, or price'}
                value={procedureQuery}
                onChange={(event) => {
                  const value = event.target.value;
                  setProcedureQuery(value);
                  const selected = availableProcedures.find((procedure) => procedure._id === newProcedurePlan.PROCEDURE_ID);
                  if (!selected || procedureChoice(selected).label !== value) {
                    setNewProcedurePlan((prev) => ({ ...prev, PROCEDURE_ID: '' }));
                  }
                }}
                options={availableProcedures.map(procedureChoice)}
                onSelect={(option) => {
                  setProcedureQuery(option.label);
                  setNewProcedurePlan((prev) => ({ ...prev, PROCEDURE_ID: option.item._id }));
                }}
              />
              {selectedCourse && (
                <p style={{ margin: '0.4rem 0 0', color: '#495057' }}>
                  {selectedCourse.SERVICE_KIND === 'Package' ? 'Package' : selectedCourse.SERVICE_KIND === 'Course' ? 'Course' : 'Single visit'}
                  {selectedCourse.SESSION_COUNT > 1 ? `, ${selectedCourse.SESSION_COUNT} sittings` : ''}. The first sitting is this visit. Remaining sittings are booked as appointments when this visit is billed.
                </p>
              )}
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Procedure Status:</label>
              <select
                name="PROCEDURE_STATUS"
                value={newProcedurePlan.PROCEDURE_STATUS}
                onChange={handleNewProcedurePlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="Planned">Planned</option>
                <option value="Scheduled">Scheduled</option>
                <option value="Completed">Completed</option>
                <option value="Overdue">Overdue</option>
              </select>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Procedure Date:</label>
              <input
                type="date"
                name="PROCEDURE_DATE"
                value={newProcedurePlan.PROCEDURE_DATE}
                onChange={handleNewProcedurePlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Procedure Slot:</label>
              <input
                type="text"
                name="PROCEDURE_SLOT"
                value={newProcedurePlan.PROCEDURE_SLOT}
                onChange={handleNewProcedurePlanChange}
                placeholder="11:30 AM - 12:00 PM"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>

            {/* Room Selection */}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Procedure Room:</label>
              <select
                name="ROOM_ID"
                value={newProcedurePlan.ROOM_ID}
                onChange={handleNewProcedurePlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                disabled={roomLoading}
              >
                <option value="">Select Room</option>
                {availableProcedureRooms.map((room) => (
                  <option key={room._id} value={room._id}>
                    {room.ROOM_NAME} (Capacity: {room.CAPACITY}, Status: {room.STATUS})
                  </option>
                ))}
              </select>
              {roomError && <p style={{ color: 'red', fontSize: '0.8em' }}>Error loading rooms: {roomError}</p>}
            </div>

            {/* Therapist Selection */}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Therapist:</label>
              <select
                name="THERAPIST_ID"
                value={newProcedurePlan.THERAPIST_ID}
                onChange={handleNewProcedurePlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                disabled={therapistLoading}
              >
                <option value="">Select Therapist</option>
                {availableTherapists.map((therapist) => (
                  <option key={therapist._id} value={therapist._id}>
                    {therapist.THERAPIST_NAME} ({therapist.SPECIALIZATION})
                  </option>
                ))}
              </select>
              {therapistError && <p style={{ color: 'red', fontSize: '0.8em' }}>Error loading therapists: {therapistError}</p>}
            </div>

              <button onClick={handleAddProcedurePlan} disabled={loading} style={{ marginTop: '10px', padding: '10px 20px', backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
                {loading ? 'Adding...' : 'Add Procedure Plan'}
              </button>
              </>}
            </div>
          )}
        </div>

        {/* Lab Test Plans Section */}
        <div style={{ marginTop: '30px' }}>
          <h4 style={sectionTitle}>Lab tests on this visit</h4>
          {labTestLoading && <p>Loading lab tests...</p>}
          {labTestError && <p style={{ color: 'red' }}>Error loading lab tests: {labTestError}</p>}

          {currentLabTestPlans.length > 0 ? (
            <div>
              <h5>Existing Lab Test Plans:</h5>
              {currentLabTestPlans.map((plan) => (
                <div key={plan._id} style={{ border: '1px dashed #ccc', padding: '10px', marginBottom: '10px' }}>
                  <p><strong>Test:</strong> {plan.LAB_TEST_ID ? `${plan.LAB_TEST_ID.TEST_NAME} (${plan.LAB_TEST_ID.TEST_CODE})` : 'N/A'}</p>
                  <p><strong>Status:</strong> {plan.TEST_STATUS}</p>
                  <p><strong>Date:</strong> {plan.TEST_DATE}</p>
                  <p><strong>Priority:</strong> {plan.PRIORITY}</p>
                  {plan.INSTRUCTIONS && <p><strong>Instructions:</strong> {plan.INSTRUCTIONS}</p>}
                  {plan.FASTING_REQUIRED && <p><strong>Fasting Required:</strong> Yes</p>}
                  {plan.COST > 0 && <p><strong>Cost:</strong> ₹{plan.COST}</p>}
                </div>
              ))}
            </div>
          ) : (
            !labTestLoading && !labTestError && <p style={{ color: '#666', marginTop: 0 }}>No lab test ordered on this visit.</p>
          )}

          {!readOnly && (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <button type="button" onClick={() => setShowLabForm((open) => !open)} style={addButton}>
                {showLabForm ? 'Hide lab form' : 'Add lab test'}
              </button>
              {showLabForm && <>
            
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Lab Test:</label>
              <SearchBox
                placeholder={labTestLoading ? 'Loading lab tests...' : 'Search lab test by name, sample, or price'}
                value={labQuery}
                onChange={(event) => {
                  const value = event.target.value;
                  setLabQuery(value);
                  const selected = availableLabTests.find((test) => test.LAB_TEST_ID === newLabTestPlan.LAB_TEST_ID);
                  if (!selected || labChoice(selected).label !== value) {
                    setNewLabTestPlan((prev) => ({
                      ...prev,
                      LAB_TEST_ID: '',
                      COST: 0,
                      FASTING_REQUIRED: false,
                      PREPARATION_NOTES: '',
                      REFERENCE_LAB: '',
                    }));
                  }
                }}
                options={availableLabTests.map(labChoice)}
                onSelect={(option) => {
                  const test = option.item;
                  setLabQuery(option.label);
                  setNewLabTestPlan((prev) => ({
                    ...prev,
                    LAB_TEST_ID: test.LAB_TEST_ID,
                    COST: test.COST || 0,
                    FASTING_REQUIRED: !!test.FASTING_REQUIRED,
                    PREPARATION_NOTES: test.PREPARATION_REQUIRED || '',
                    REFERENCE_LAB: test.REFERENCE_LAB || '',
                  }));
                }}
              />
              {labTestError && <p style={{ color: 'red', fontSize: '0.8em' }}>Error loading lab tests: {labTestError}</p>}
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Test Status:</label>
              <select
                name="TEST_STATUS"
                value={newLabTestPlan.TEST_STATUS}
                onChange={handleNewLabTestPlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="Ordered">Ordered</option>
                <option value="Scheduled">Scheduled</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Test Date:</label>
              <input
                type="date"
                name="TEST_DATE"
                value={newLabTestPlan.TEST_DATE}
                onChange={handleNewLabTestPlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Test Time:</label>
              <input
                type="time"
                name="TEST_SLOT"
                value={newLabTestPlan.TEST_SLOT}
                onChange={handleNewLabTestPlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Priority:</label>
              <select
                name="PRIORITY"
                value={newLabTestPlan.PRIORITY}
                onChange={handleNewLabTestPlanChange}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="Low">Low</option>
                <option value="Normal">Normal</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Instructions:</label>
              <textarea
                name="INSTRUCTIONS"
                value={newLabTestPlan.INSTRUCTIONS}
                onChange={handleNewLabTestPlanChange}
                placeholder="Special instructions for the test..."
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minHeight: '60px' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="FASTING_REQUIRED"
                  checked={newLabTestPlan.FASTING_REQUIRED}
                  onChange={handleNewLabTestPlanChange}
                />
                Fasting Required
              </label>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Preparation Notes:</label>
              <textarea
                name="PREPARATION_NOTES"
                value={newLabTestPlan.PREPARATION_NOTES}
                onChange={handleNewLabTestPlanChange}
                placeholder="Preparation instructions for the patient..."
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minHeight: '60px' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Reference Lab:</label>
              <input
                type="text"
                name="REFERENCE_LAB"
                value={newLabTestPlan.REFERENCE_LAB}
                onChange={handleNewLabTestPlanChange}
                placeholder="Lab where test will be performed"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Cost (₹):</label>
              <input
                type="number"
                name="COST"
                value={newLabTestPlan.COST}
                onChange={handleNewLabTestPlanChange}
                min="0"
                step="0.01"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="INSURANCE_COVERED"
                  checked={newLabTestPlan.INSURANCE_COVERED}
                  onChange={handleNewLabTestPlanChange}
                />
                Insurance Covered
              </label>
            </div>

              <button onClick={handleAddLabTestPlan} disabled={loading} style={{ marginTop: '10px', padding: '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
                {loading ? 'Adding...' : 'Add Lab Test Plan'}
              </button>
              </>}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1100,
};

const modalContentStyle = {
  backgroundColor: 'white',
  padding: '24px',
  borderRadius: '8px',
  width: '94%',
  maxWidth: '1080px',
  maxHeight: '92%',
  overflowY: 'auto',
  position: 'relative',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
};

const panelStyle = { marginTop: '1rem', padding: '1rem', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#fff' };
const sectionTitle = { margin: '0 0 0.75rem', color: '#1f4d40' };
const fieldStyle = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' };
const addButton = { marginBottom: '0.75rem', padding: '8px 12px', backgroundColor: 'white', color: '#0f766e', border: '1px solid #0f766e', borderRadius: '4px', cursor: 'pointer' };

function vitalSummary(vitals) {
  if (!vitals) return '—';
  const parts = [];
  if (vitals.BLOOD_PRESSURE) parts.push(vitals.BLOOD_PRESSURE);
  if (vitals.PULSE) parts.push(`P ${vitals.PULSE}`);
  if (vitals.TEMPERATURE) parts.push(`${vitals.TEMPERATURE}°F`);
  if (vitals.SPO2) parts.push(`SpO2 ${vitals.SPO2}%`);
  if (vitals.WEIGHT) parts.push(`${vitals.WEIGHT} kg`);
  return parts.length ? parts.join(', ') : '—';
}

function ChartTable({ headers, rows }) {
  if (!rows.length) return <p style={{ margin: 0, color: '#6c757d' }}>Nothing recorded yet.</p>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr>
            {headers.map((header) => <th key={header} style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'left', backgroundColor: '#f8f9fa' }}>{header}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, cellIndex) => <td key={cellIndex} style={{ padding: '8px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top' }}>{cell || '—'}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DoctorAppointmentDetailModal;
